/**
 * frontend/src/pages/valuation/_shared.tsx
 *
 * VALUATOR-UI-1 — pieces shared by the four Valuation screens
 * (profile select → intake → output with provenance → sign-off state).
 *
 * Split out under docs/FILE_SIZE_POLICY.md so no screen file carries
 * boilerplate it does not own.
 *
 * Two things in here are worth reading before changing them:
 *
 * 1. `VALUATION_STATUSES` is a CLIENT vocabulary. `ValuationEngagement.status`
 *    is a free-form `String(30)` on the row with no server-side state machine,
 *    so these labels are a convention this UI writes through the existing
 *    PATCH endpoint — not a workflow the API enforces. Anything that must be
 *    enforced (who may assert a conclusion of value, whether an override is in
 *    range) is enforced server-side and surfaced here, never decided here.
 *
 * 2. `runCache` keeps the last run payload in sessionStorage. The API persists
 *    only the BAND on the engagement row; the provenance — which signed
 *    profile, which multiple, which overrides — comes back in the run response
 *    and is not re-readable afterwards. Rather than invent a provenance the
 *    server never stored, the output screen shows the cached run and says
 *    plainly when it is showing the persisted band without it.
 *
 * The tuning screen's inputs are NOT cached here: Methodology and Sign-off both
 * read the saved draft (`useValuationDraft`), never a browser copy.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Badge } from '@/components/ui/badge'
import {
  VALUATION_CONCLUSION_ROLES,
  VALUATION_OVERRIDE_ROLES,
  VALUATION_READ_ROLES,
  type ValuationEngagement,
  type ValueKind,
} from '@/types/valuation'
import type { RecomputeResponse } from '@/types/valuationB3'
import { VALUATION_KEY_PREFIX } from './_valuationSession'
import { LOCKED_MESSAGE, isLocked, stepNext, stepsAt, stepsFor, type PathStepKey } from './_steps'
import { useStepContext, useStepMover } from './_stepContext'

// ── Entitlement helpers (mirror apps/api/authz.py; the server still decides) ──

export function useValuationEntitlement() {
  const { user } = useAuth()
  const role = user?.role as string | undefined
  return {
    role,
    canView: !!role && (VALUATION_READ_ROLES as readonly string[]).includes(role),
    /**
     * May assert/sign a CONCLUSION of value — the credentialed-only capability.
     * VAL-SIGNOFF-GATE-1: this is NOT the gate for whether a calculation
     * engagement may be recorded — an owner records without this — only for
     * whether the record may be a conclusion, or signed/printed at all.
     */
    canConclude: !!role && (VALUATION_CONCLUSION_ROLES as readonly string[]).includes(role),
    /** May adjust a calculated input on their own engagement (not credential-gated). */
    canOverride: !!role && (VALUATION_OVERRIDE_ROLES as readonly string[]).includes(role),
  }
}

// ── Client status vocabulary ─────────────────────────────────────────────────

export const VALUATION_STATUSES = {
  intake_in_progress: 'Intake in progress',
  calculated: 'Calculated — awaiting review',
  in_cpa_review: 'In Valuation Signer review',
  signed_off: 'Signed off',
} as const

export type ValuationStatus = keyof typeof VALUATION_STATUSES

export function statusLabel(status: string): string {
  return (VALUATION_STATUSES as Record<string, string>)[status] ?? status
}

// ── Formatting (PLAT-UTILS-1 R3) ─────────────────────────────────────────────
// money/pct/fracPct/multiple now live in the shared Utils package
// (@/utils/format), not here — this is a re-export, not a definition, kept
// only so `ValuationIntake.tsx` (fenced for VAL-NAV-STATE-1) does not need an
// import-path edit. New callers should import from '@/utils/format' directly.
export { money, pct, fracPct, multiple } from '@/utils/format'

export function valueKindLabel(kind: ValueKind): string {
  return kind === 'concluded'
    ? 'Conclusion of value (valuation engagement)'
    : 'Calculated value (calculation engagement)'
}

// ── Run cache (see module docstring) ─────────────────────────────────────────

const RUN_KEY = (id: string) => `${VALUATION_KEY_PREFIX}signed:${id}`

// VAL-STALE-RUN-1: each cached run carries the app version that ran it; a tab
// left open across a deploy must not keep showing the older build's result.
const APP_VERSION = String(import.meta.env.VITE_APP_VERSION ?? '')

export const runCache = {
  save(id: string, result: RecomputeResponse) {
    try {
      sessionStorage.setItem(RUN_KEY(id), JSON.stringify({ v: APP_VERSION, run: result }))
    } catch {
      /* private mode / quota — the output screen degrades to the persisted band */
    }
  },
  load(id: string): RecomputeResponse | null {
    try {
      const raw = sessionStorage.getItem(RUN_KEY(id))
      if (!raw) return null
      const stored = JSON.parse(raw) as { v?: string; run?: RecomputeResponse }
      if (stored.v === APP_VERSION && stored.run) return stored.run
      sessionStorage.removeItem(RUN_KEY(id)) // another build's run (or pre-version format)
      return null
    } catch {
      return null
    }
  },
  clear(id: string) {
    try {
      sessionStorage.removeItem(RUN_KEY(id))
    } catch {
      /* nothing to do */
    }
  },
}

/** Read the cached run once on mount. */
export function useCachedRun(id: string | undefined): RecomputeResponse | null {
  const [run, setRun] = useState<RecomputeResponse | null>(null)
  useEffect(() => {
    setRun(id ? runCache.load(id) : null)
  }, [id])
  return run
}

// ── Stored code -> label (VAL-WALK-SCREENS-1 W5) ─────────────────────────────

const BASIS_LABELS: Record<string, string> = { adjusted_ebitda: 'Adjusted EBITDA', sde: 'SDE' }
const SNAPSHOT_STATUS_LABELS: Record<string, string> = { final: 'Final', superseded: 'Replaced' }

/** The earnings basis as a person reads it; an unknown code is printed as stored. */
export function basisLabel(code: string): string {
  return BASIS_LABELS[code] ?? code
}

/** A snapshot's status as a person reads it; an unknown code is printed as stored. */
export function snapshotStatusLabel(code: string): string {
  return SNAPSHOT_STATUS_LABELS[code] ?? code
}

// ── Stepper ──────────────────────────────────────────────────────────────────
//
// VAL-PATH-ROUTES (P1): the bar draws the one step list (_steps.ts) for this
// role — the same steps, labels and locks as the left nav. Role and
// engagement come from props, else from the ValuationStepGuard around the
// route, else the owner path with nothing locked.

export type StepKey = PathStepKey | 'profile'

/** The step after `current` along this role's path (null at the end). */
export function nextStepPath(current: StepKey, id?: string, role?: string): string | null {
  if (!id) return '/valuation'
  if (current === 'profile') return stepsFor(role)[0].route(id)
  return stepNext(current, role, id)
}

export function ValuationStepper({
  current,
  engagementId,
  role,
  engagement,
}: {
  current: StepKey
  engagementId?: string
  role?: string
  engagement?: ValuationEngagement | null
}) {
  const ctx = useStepContext()
  const mover = useStepMover()
  const who = role ?? ctx?.role
  const e = engagement ?? ctx?.engagement ?? null
  const steps = stepsAt(current, who)
  const purpose = steps.find(s => s.key === current)?.purpose
  return (
    <nav aria-label="Valuation steps" className="mb-6">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {steps.map((step, i) => {
          const active = step.key === current
          const locked = isLocked(step.key, e, who)
          const reachable = !!engagementId && !locked
          const content = (
            <span
              aria-current={active ? 'step' : undefined}
              title={locked ? LOCKED_MESSAGE : undefined}
              className={
                active
                  ? 'font-semibold text-[#0d1f3c]'
                  : reachable
                    ? 'text-gray-500 hover:text-[#0d1f3c]'
                    : 'text-gray-300'
              }
            >
              {i + 1}. {step.label}
            </span>
          )
          return (
            <li key={step.key} className="flex items-center gap-2 whitespace-nowrap">
              {reachable && !active && engagementId
                ? (
                  <Link
                    to={step.route(engagementId)}
                    onClick={ev => {
                      // VAL-PATH-FOOTER (P2a): save the open step first (U2).
                      const move = mover?.current
                      if (!move) return
                      ev.preventDefault()
                      void move(step.route(engagementId))
                    }}
                  >
                    {content}
                  </Link>
                )
                : content}
              {i < steps.length - 1 && <span className="text-gray-300">›</span>}
            </li>
          )
        })}
      </ol>
      {purpose && (
        <p data-testid="step-purpose" className="mt-2 text-sm text-gray-600">{purpose}</p>
      )}
    </nav>
  )
}

// ── Small presentational helpers ─────────────────────────────────────────────

// VAL-FOLLOWUP-1: an owner (valuation_owner) sees the owner-approved title
// (10-22 / 10-29); a signer and every concluded engagement are unchanged.
export function ValueKindBadge({ kind, owner = false }: { kind: ValueKind; owner?: boolean }) {
  if (kind === 'concluded') return <Badge className="bg-[#0d1f3c] text-white">Conclusion of value</Badge>
  return <Badge variant="secondary">{owner ? "Owner's Estimated Value" : 'Calculated value'}</Badge>
}

export function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'signed_off' ? 'bg-green-100 text-green-800'
      : status === 'in_cpa_review' ? 'bg-amber-100 text-amber-900'
        : 'bg-gray-100 text-gray-700'
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{statusLabel(status)}</span>
}

/** A labelled read-only fact. Used heavily on the provenance screen. */
export function Fact({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-sm text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}
